'use client';

import type { IconButtonProps, SpanProps } from '@chakra-ui/react';
import type { ThemeProviderProps } from 'next-themes';

import { useMutation } from '@apollo/client/react';
import { ClientOnly, IconButton, Skeleton, Span } from '@chakra-ui/react';
import { ThemeProvider } from 'next-themes';
import * as React from 'react';
import { LuMoon, LuSun } from 'react-icons/lu';

import { UPDATE_MY_USER_SETTINGS } from '#/graphql/mutations/update-my-user-settings';
import { useAppConfig } from '#/providers/app-config-provider';
import { useUser } from '#/providers/user-provider';

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface ColorModeProviderProps extends ThemeProviderProps {}

export function ColorModeProvider(props: ColorModeProviderProps) {
  return (
    <ThemeProvider attribute="class" disableTransitionOnChange {...props} />
  );
}

export type ColorMode = 'light' | 'dark';

export interface UseColorModeReturn {
  colorMode: ColorMode;
  setColorMode: (colorMode: ColorMode) => void;
  toggleColorMode: () => void;
}

export function useColorMode(): UseColorModeReturn {
  const { config, setColorModeFromServer } = useAppConfig();
  const { user } = useUser();
  const [updateMyUserSettings] = useMutation(UPDATE_MY_USER_SETTINGS);

  const colorMode = (config.colorMode ?? 'light') as ColorMode;

  const persistColorMode = React.useCallback(
    (mode: ColorMode) => {
      setColorModeFromServer(mode);
      if (user) {
        void updateMyUserSettings({
          variables: { input: { colorMode: mode } },
          refetchQueries: ['Me'],
        });
      }
    },
    [user, setColorModeFromServer, updateMyUserSettings],
  );

  const setColorMode = React.useCallback(
    (mode: ColorMode) => {
      persistColorMode(mode);
    },
    [persistColorMode],
  );
  const toggleColorMode = React.useCallback(() => {
    persistColorMode(colorMode === 'dark' ? 'light' : 'dark');
  }, [colorMode, persistColorMode]);
  return {
    colorMode,
    setColorMode,
    toggleColorMode,
  };
}

export function useColorModeValue<T>(light: T, dark: T) {
  const { colorMode } = useColorMode();
  return colorMode === 'dark' ? dark : light;
}

export function ColorModeIcon() {
  const { colorMode } = useColorMode();
  return colorMode === 'dark' ? <LuMoon /> : <LuSun />;
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface ColorModeButtonProps extends Omit<IconButtonProps, 'aria-label'> {}

export const ColorModeButton = React.forwardRef<
  HTMLButtonElement,
  ColorModeButtonProps
>(function ColorModeButton(props, ref) {
  const { toggleColorMode } = useColorMode();
  return (
    <ClientOnly fallback={<Skeleton boxSize="8" />}>
      <IconButton
        onClick={toggleColorMode}
        variant="ghost"
        aria-label="Toggle color mode"
        size="sm"
        ref={ref}
        {...props}
        css={{
          _icon: {
            width: '5',
            height: '5',
          },
        }}
      >
        <ColorModeIcon />
      </IconButton>
    </ClientOnly>
  );
});

export const LightMode = React.forwardRef<HTMLSpanElement, SpanProps>(
  function LightMode(props, ref) {
    return (
      <Span
        color="fg"
        display="contents"
        className="chakra-theme light"
        colorPalette="gray"
        colorScheme="light"
        ref={ref}
        {...props}
      />
    );
  },
);

export const DarkMode = React.forwardRef<HTMLSpanElement, SpanProps>(
  function DarkMode(props, ref) {
    return (
      <Span
        color="fg"
        display="contents"
        className="chakra-theme dark"
        colorPalette="gray"
        colorScheme="dark"
        ref={ref}
        {...props}
      />
    );
  },
);
